import * as R from 'ramda';
import { makeString } from './strings';
import { UNIX_EPOCH_STR } from './time';


/**
 * Returns true if the given object, string, or array is null/undefined or is empty. Objects are empty if they don't
 * have any own properties.
 */
export const isNilOrEmpty = (testCase: any): boolean => R.isNil(testCase) || R.isEmpty(testCase);


/**
 * Returns whether or not the given input is a function.
 */
export const isFunction = (obj: any): boolean => !!(obj && obj.constructor && obj.call && obj.apply);


/**
 * This is a noop function for when a function is expected but you do not want an action to be taken. It was used in
 * a few places, so I moved it to here.
 */
export const doNothing = () => {};


/**
 * Makes an immutable update to the given input by setting the given key to the given value.
 * This effectively does the same thing as R.assoc, but uses better type inference, which produces better errors.
 */
export const assoc = <TInput, TKey extends keyof TInput>(key: TKey, newValue: TInput[TKey], input: TInput): TInput => ({
  ...input,
  [key]: newValue,
});


/**
 * Makes an immutable update to the given object by removing the given key and its associated value from the object.
 */
export const dissoc = <TInput, TKey extends keyof TInput>( key: TKey, input: TInput ): TInput => {
  const copy = { ...input };
  delete copy[key];
  return copy;
};


/**
 * Uses Java's object hash algorithm to hash the given string and return a number. This is not a cryptographic hash.
 */
export const getHash = (value: any): number => {
  const str = makeString(value);

  let chr;
  let hash = 0;

  for (let i = 0; i < str!.length; i++) {
    chr  = str!.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }

  return hash;
};


/**
 * Takes an array, predicate and functor, and applies the functor to the first element in the array for which the
 * predicate returns true. Immutable: returns a new array with the original array unaltered (shallow copy on write).
 *
 * In layman's terms: updates the matching item in the array using the given update function and returning a new array.
 * If no match is found, returns the unaltered array.
 *
 * @param updater - The function to run on the first match found (and only the first match found)
 * @param matcher - The predicate that says what is and isn't a match
 * @param values - The array of values to operate on
 */
export const updateIn = <T>(
  updater: (value: T) => T,
  matcher: (value: T) => boolean,
  values: T[],
): T[] => {
  const matchIndex = R.findIndex(matcher, values);

  if (matchIndex === -1) {
    return values;
  }

  return R.insert(
    matchIndex,
    updater(values[matchIndex]),
    R.remove(matchIndex, 1, values),
  );
};


/**
 * Takes a value, an array of the same type and a predicate, and either replaces the first matching element in the array
 * according to the predicate with the new value, or appends the new value to the end of the array. Returns a new array
 * without altering the given array.
 * @param matcher - The predicate that says whether or not two items have equality
 * @param values - The list of values
 * @param value - The new value to add/update
 */
export const upsertIn = <T>(
  matcher: (value: T) => boolean,
  values: T[],
  value: T,
): T[] => {
  const matchIndex = R.findIndex(matcher, values);

  if (matchIndex === -1) {
    return values.concat(value);
  }

  return R.insert(matchIndex, value, R.remove(matchIndex, 1, values));
};


/**
 * Takes an array, accessor function and sort direction; returns a new array whose elements are sorted by the values
 * returned by the accessor function. Note that the accessor could calculate something about array elements instead of
 * just being a way to get values off elements.
 * @param accessor - Function accepting a T and returning a simple value to sort by (number/string)
 * @param sortAsc - True if it should sort ascending (A-Z) or false for descending (Z-A)
 * @param values - The array to clone and sort
 */
export const sortBy = <T>(
  accessor: (element: T) => number | string | Date,
  sortAsc: boolean,
  values: T[],
): T[] => values.slice(0) // Slice to clone
  .sort((a, b) => {
    const aValue = accessor(a);
    const bValue = accessor(b);

    const sortValue = aValue !== bValue ? aValue > bValue ? 1 : -1 : 0;

    // Invert direction if ascending
    return sortValue * (sortAsc ? 1 : -1);
  });


/**
 * Takes a property name, an array, and a direction, and returns a new array whose elements are sorted by that property.
 * Case insensitivity is recommend for strings for sorting consistency so that 'a' is sorted before 'B'
 * when sorting alphabetically.
 * @param property - The string name of the property by which to sort. Choose properties with an obvious ordinality
 * @param isCaseInsensitive - It true, this will convert the prop to a string and lowercase it for comparison.
 * @param sortAsc - Sort in an ascending direction (A-Z) or descending (Z-A)
 * @param values - The array to sort
 */
const sortByProperty = (
  property: string,
  isCaseInsensitive: boolean,
  sortAsc: boolean,
  values: any[],
) => {
  if (isCaseInsensitive) {
    return sortBy(element => makeString((element[property] || '')).toLowerCase(), sortAsc, values);
  }

  return sortBy(element => element[property], sortAsc, values);
};


/**
 * Takes a property name, an array, and a direction, and returns a new array whose elements are sorted by that property.
 * Curried for convenience.
 * @param property - The string name of the property by which to sort. Choose properties with an obvious ordinality
 * @param isCaseInsensitive - It true, this will convert the prop to a string and lowercase it for comparison.
 * @param sortAsc - Sort in an ascending direction (A-Z) or descending (Z-A)
 * @param values - The array to sort
 */
export const sortByProp = R.curry(sortByProperty);

export const sortByEmailProp = R.curry(
  ( 
    isCaseInsensitive: boolean,
    sortAsc: boolean,
    values: any[],
   ) => {
    if (isCaseInsensitive) {
      return sortBy(element => element['hasEmail'] ? makeString((element['email'] || '')).toLowerCase() : '', sortAsc, values);
    }

    return sortBy(element => element['hasEmail'] ? element['email'] : '', sortAsc, values);
   }
);

/**
 * Takes an array and the property to convert to dates and sort. We have a lot of dates that arrive as strings which,
 * sadly, won't sort correctly, so we must convert them to dates first and then sort
 * @param property - The string name of the property containing the date string
 * @param sortAsc - Sort in an ascending direction (1/1/2000-1/1/2001) or descending (1/1/2001-1/1/2000)
 * @param values - The array to sort
 */
export const sortByDateProp = R.curry(
  (
    property: string,
    sortAsc: boolean,
    values: any[],
  ) => sortBy(element => new Date(element[property] || UNIX_EPOCH_STR), sortAsc, values),
);


/**
 * Given an array of string arrays (paths) and an object:
 * Returns true if all paths resolve as true.
 * Returns false if at least one path is missing from the object.
 * This is uses Ramda's reduce and hasPath functions.
 * @param propPaths - array of string arrays of paths to check
 * @param obj - Object that paths are being checked on
 */
export const hasPaths = (propPaths: string[][], obj: any): boolean => propPaths.reduce(
  (running, next) => running && R.hasPath(next, obj),
  true,
);

/**
 * Wraps the given predicate in another predicate that returns the result of not-ing the first predicate.
 */
export const not = <T>(predicate: (item: T) => boolean) => (item: T): boolean => !predicate(item);


function fallbackValue(value: any): string | number {
  if (value instanceof Date) return 0;
  if (typeof value === "number") return Number.NEGATIVE_INFINITY;
  return ""; // string fallback
}

function normalizeValue(value: any): string | number {
  if (value == null) {
    return fallbackValue(value);
  }

  // Number-like
  if (typeof value === "number") {
    return value;
  }

  // Date object
  if (value instanceof Date) {
    return value.getTime();
  }

  // Date-like string
  if (typeof value === "string" && !isNaN(Date.parse(value))) {
    return new Date(value).getTime();
  }

  // Default: string, case-insensitive
  return String(value).toLowerCase();
}

/**
 * Sorts a property whose value is an array of objects by the FIRST object in the array.
 * Automatically detects whether the sort property is string, number, or date-like.
 */
export function sortArrayOfObjectsProperty(
  arrayProperty: string,
  objectProperty: string,
  sortAsc: boolean,
  values: any[],
  useConcatenatedString: boolean = false
) {
  return sortBy(element => {
    const arr = element[arrayProperty];

    if (!Array.isArray(arr) || arr.length === 0) {
      return fallbackValue(undefined);
    }

    // Concatenate ALL values into a CSV string (day of week sort)
    if (useConcatenatedString) {
      const allValues = arr
        .map(obj => normalizeValue(obj?.[objectProperty]))
        .filter(v => v !== undefined && v !== null);

      const concatenated = allValues.join(","); // "A,B,C"

      return concatenated || fallbackValue(undefined);
    }

    // (first object only)
    const firstObj = arr[0];
    const rawVal = firstObj?.[objectProperty];

    return normalizeValue(rawVal);
  }, sortAsc, values);
}
